import type { ComponentChildren } from "preact";
import { useContext, useEffect, useRef, useState } from "preact/hooks";
import { Box } from "../ui/box";
import { appearanceSentence, personalitySentence } from "./details";
import type { Drafts } from "./drafts";
import { EditContext } from "./edits";
import { useClipped } from "./fold";
import { foldOf } from "./folds";
import type { Labels } from "./labels";
import { Mark } from "./pop";
import { ACTION_ROW, Button, TextButton } from "./controls";
import { postForm } from "./sheet_menu";
import type { SheetView } from "./sheet_view";

const FACT = "min-w-0";
const FACT_LABEL = "text-xs font-bold uppercase tracking-wider text-ink-muted";

function Fact({ label, full = false, children }: { label: string; full?: boolean; children: ComponentChildren }) {
	return (
		<div class={`${FACT}${full ? " cs-full" : ""}`}>
			<dt class={FACT_LABEL}>{label}</dt>
			<dd class="mt-0.5 text-[15.5px]">{children}</dd>
		</div>
	);
}

// The background clamps to a few lines until asked for.
function Background({ text, labels }: { text: string; labels: Labels }) {
	const [open, setOpen] = useState(false);
	const [ref, clipped] = useClipped<HTMLParagraphElement>(!open, text);
	return (
		<>
			<p ref={ref} class={`whitespace-pre-line ${open ? "" : "line-clamp-3"}`}>
				{text}
			</p>
			{(clipped || open) && (
				<button type="button" class="text-sm font-bold text-link hover:text-link-hover" aria-expanded={open} onClick={() => setOpen(!open)}>
					{open ? labels.details.background_less : labels.details.background_more}
				</button>
			)}
		</>
	);
}

export function Details({ view, labels }: { view: SheetView; labels: Labels }) {
	const { sheet } = view;
	const details = sheet.details;
	const languages = (details?.languages ?? []).filter((language) => language.trim()).join(", ");
	const appearance = details ? appearanceSentence(sheet.name, details.appearance, labels) : "";
	const personality = details ? personalitySentence(details.personality, labels) : "";
	const background = details?.background?.trim() ?? "";
	const dash = <span class="text-ink-muted">—</span>;
	return (
		<Box icon="identity" title={labels.boxes.details} fold={foldOf(view, "details", labels.boxes.details, labels)} class="px-3.5 pb-3">
			<dl class="cs-facts mt-1">
				<Fact label={labels.details.field_alignment}>{sheet.alignment ? labels.alignments[sheet.alignment] : dash}</Fact>
				<Fact label={labels.details.field_languages}>{languages || dash}</Fact>
				<Fact label={labels.details.field_appearance}>{appearance || dash}</Fact>
				<Fact label={labels.details.field_personality}>{personality || dash}</Fact>
				<Fact label={labels.details.field_background} full>
					{background ? <Background text={background} labels={labels} /> : dash}
				</Fact>
			</dl>
		</Box>
	);
}

export function Notes({ view, labels, maxLength, drafts }: { view: SheetView; labels: Labels; maxLength?: number; drafts: Drafts }) {
	const edits = useContext(EditContext);
	const notes = view.sheet.notes;
	return (
		<Box icon="scroll" title={labels.boxes.notes} fold={foldOf(view, "notes", labels.boxes.notes, labels)} class="px-3.5 pb-3">
			{edits ? (
				<NotesEditor
					live={notes}
					save={edits.setNotes}
					drafts={drafts}
					label={labels.boxes.notes}
					placeholder={labels.edit.notes_placeholder}
					maxLength={maxLength}
				/>
			) : notes.trim() ? (
				<p class="whitespace-pre-wrap text-[15.5px]">{notes.trim()}</p>
			) : (
				<p class="italic text-ink-muted">{labels.details.notes_empty}</p>
			)}
		</Box>
	);
}

const IDLE_MS = 1000;

// The text the player types is theirs until it is sent: while the field has focus, or holds words not yet
// sent, nothing arriving from the server (a diff, a resync) replaces it. Once sent, the pending write's own
// value stands in for the server's until it settles.
export function NotesEditor({
	live,
	save: send,
	drafts,
	label,
	placeholder,
	rows = 12,
	height = "min-h-56",
	maxLength,
}: {
	live: string;
	save: (notes: string) => void;
	// The island's registry: it flushes the text still waiting for its second of quiet when the island closes.
	drafts: Drafts;
	label: string;
	placeholder: string;
	rows?: number;
	height?: string;
	maxLength?: number;
}) {
	const [draft, setDraft] = useState<string | null>(null);
	const latest = useRef("");
	const dirty = useRef(false);
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	// The unmount flush below was made on the first render; the page's writer may have been replaced since.
	const sender = useRef(send);
	sender.current = send;

	const save = () => {
		clearTimeout(timer.current);
		if (!dirty.current) return;
		dirty.current = false;
		sender.current(latest.current);
	};
	useEffect(() => {
		const forget = drafts.register(save);
		return () => {
			forget();
			save();
		};
	}, []);

	return (
		<textarea
			aria-label={label}
			placeholder={placeholder}
			rows={rows}
			maxLength={maxLength}
			value={draft ?? live}
			class={`mt-1 ${height} w-full resize-y rounded-lg border border-line bg-surface-raised p-2 text-[15.5px] leading-snug text-ink`}
			onFocus={() => {
				latest.current = live;
				setDraft(live);
			}}
			onInput={(event) => {
				latest.current = event.currentTarget.value;
				dirty.current = true;
				setDraft(latest.current);
				clearTimeout(timer.current);
				timer.current = setTimeout(save, IDLE_MS);
			}}
			onBlur={() => {
				save();
				setDraft(null);
			}}
		/>
	);
}

export function HeroicAction({ view, labels }: { view: SheetView; labels: Labels }) {
	const edits = useContext(EditContext);
	const used = view.sheet.heroic_action_used === true;
	return (
		<Box icon="wings" title={labels.boxes.heroic_action} fold={foldOf(view, "heroic", labels.boxes.heroic_action, labels)} class="px-3.5 pb-3">
			<div class="flex items-start gap-2 text-sm">
				<Mark on={used} label={`${labels.boxes.heroic_action}: ${used ? labels.boxes.used : labels.boxes.available}`} onToggle={edits?.setHeroicAction} />
				<p class={used ? "text-ink-muted line-through" : ""}>
					<b>{labels.boxes.heroic_once}</b> {labels.boxes.heroic_description}
				</p>
			</div>
		</Box>
	);
}

// A retainer's way to a first-level character: asks here, then posts like the old sheet's button, never a browser dialog.
export function BecomeAdventurer({ view, labels, url }: { view: SheetView; labels: Labels; url: string }) {
	const [confirming, setConfirming] = useState(false);
	const button = useRef<HTMLButtonElement>(null);
	const text = labels.become_adventurer;
	const cancel = () => {
		setConfirming(false);
		button.current?.focus();
	};
	return (
		<Box icon="levelUp" title={text.title} fold={foldOf(view, "become_adventurer", text.title, labels)} class="px-3.5 pb-3">
			<div class="flex flex-col items-center gap-3 text-center">
				<p role="status" class="text-sm text-ink-muted">
					{confirming ? text.confirm : text.description}
				</p>
				<div class={ACTION_ROW}>
					<Button
						tone="secondary"
						icon="levelUp"
						label={text.button}
						buttonRef={button}
						onClick={(trigger) => {
							if (!confirming) return setConfirming(true);
							trigger.disabled = true;
							postForm(url);
						}}
					/>
					{confirming && <TextButton label={labels.edit.cancel} onClick={cancel} />}
				</div>
			</div>
		</Box>
	);
}
